import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { TrendingUp, Star, User } from 'lucide-react';
import { useOnTheComeUp } from '@/hooks/useOnTheComeUp';
import { Link } from 'react-router-dom';
import { format } from 'date-fns';

interface OnTheComeUpSectionProps {
  stateProvince: string;
  country?: string;
}

const OnTheComeUpSection: React.FC<OnTheComeUpSectionProps> = ({
  stateProvince,
  country = 'United States',
}) => {
  const { players, loading, error, weekStart } = useOnTheComeUp({
    stateProvince,
    country,
  });

  if (loading) {
    return (
      <Card className="bg-card border-border">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-foreground">
            <TrendingUp className="h-5 w-5 text-primary" />
            On the Come Up
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {[1, 2, 3].map(i => (
              <Skeleton key={i} className="h-32 w-full rounded-lg" />
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="bg-card border-border">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-foreground">
            <TrendingUp className="h-5 w-5 text-primary" />
            On the Come Up
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-muted-foreground">Unable to load featured players. Please try again later.</p>
        </CardContent>
      </Card>
    );
  }

  const hasPlayers = players.length > 0;

  return (
    <Card className="bg-gradient-to-br from-card to-card/80 border-border">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-foreground">
            <TrendingUp className="h-5 w-5 text-primary" />
            On the Come Up
            <Star className="h-4 w-4 text-yellow-500 fill-yellow-500" />
          </CardTitle>
          <Badge variant="outline" className="text-xs">
            Week of {format(new Date(weekStart), 'MMM d')}
          </Badge>
        </div>
        <p className="text-sm text-muted-foreground">
          Rising stars selected weekly in {stateProvince}
        </p>
      </CardHeader>
      <CardContent>
        {!hasPlayers ? (
          <div className="text-center py-8">
            <Star className="h-12 w-12 text-muted-foreground/30 mx-auto mb-4" />
            <p className="text-muted-foreground">No featured players this week yet.</p>
            <p className="text-sm text-muted-foreground mt-1">
              Nominations are reviewed weekly by PL Staff
            </p>
            <Button variant="outline" className="mt-4" asChild>
              <Link to="/nominate">Nominate a Player</Link>
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {players.map((player, index) => (
              <Link
                key={player.id}
                to={`/${player.username}`}
                className="block group"
              >
                <div className="relative p-4 bg-muted/30 rounded-lg hover:bg-muted/50 transition-all group-hover:scale-[1.02] border border-transparent hover:border-primary/30">
                  {/* Rank Badge */}
                  <div className="absolute -top-2 -left-2 w-8 h-8 bg-primary rounded-full flex items-center justify-center text-primary-foreground font-bold text-sm">
                    {index + 1}
                  </div>
                  
                  <div className="flex flex-col items-center text-center pt-2">
                    <Avatar className="h-16 w-16 border-2 border-primary/30">
                      <AvatarImage src={player.profilePhotoUrl || ''} alt={player.username} />
                      <AvatarFallback className="bg-muted">
                        <User className="h-8 w-8 text-muted-foreground" />
                      </AvatarFallback>
                    </Avatar>
                    
                    <h4 className="font-semibold text-foreground mt-3">
                      {player.firstName && player.lastName 
                        ? `${player.firstName} ${player.lastName}`
                        : player.username
                      }
                    </h4>
                    
                    <p className="text-xs text-muted-foreground">@{player.username}</p>
                    
                    <div className="flex flex-wrap gap-1 mt-2 justify-center">
                      {player.sport && (
                        <Badge variant="secondary" className="text-xs">
                          {player.sport}
                        </Badge>
                      )}
                      {player.position && (
                        <Badge variant="outline" className="text-xs">
                          {player.position}
                        </Badge>
                      )}
                    </div>
                    
                    {player.nominationReason && (
                      <p className="text-xs text-muted-foreground mt-2 line-clamp-2">
                        "{player.nominationReason}"
                      </p>
                    )}
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
        
        {hasPlayers && (
          <div className="mt-4 text-center">
            <Button variant="ghost" size="sm" className="text-primary" asChild>
              <Link to="/nominate">
                Know someone rising? Nominate them →
              </Link>
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default OnTheComeUpSection;
